import { Card, CardBody } from "@chakra-ui/react";
import AppPage from "layouts/AppPage";
import { useParams } from "react-router";
import { meetingKeys, useMeetingDetailQuery } from "features/Resources/Meeting/hooks";
import QueryError from "components/Error/QueryError";
import ActionPageNewHireHeader from "features/Resources/Shared/Components/ActionPageNewHireHeader";
import { usePlatformEventCable, CollapsibleHistory } from "features/PlatformEvents";
import MeetingForm from "features/Resources/Meeting/Layout/MeetingForm";
import PastMeeting from "features/Resources/Meeting/Layout/PastMeeting";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import { INTERNAL_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { newHireJourneyPathById } from "helpers/url";
import { truncateEnd } from "helpers/string";

export default function MeetingPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error: queryError, isError } = useMeetingDetailQuery(id);
  const { data: newHireJourney, isFetched } = useNewHireJourneyDetailQuery(data?.newHireJourneyId);

  usePlatformEventCable({
    entityType: "Meeting",
    entityId: data?.id,
    enabled: !!data?.id,
    detailQueryKeys: data?.id ? meetingKeys.detail(data.id) : undefined,
  });

  if (!id) {
    return <AppPage>Meeting not found</AppPage>;
  }

  if (isLoading) {
    return <AppPage>Loading...</AppPage>;
  }

  if (!data && isError) {
    return <QueryError queryError={queryError} resourceName="Meeting" id={id} />;
  }

  return (
    <AppPage>
      <CaminoBreadcrumbs
        breadcrumbInfos={[
          { name: "New Hire Journeys", toUrl: INTERNAL_TEAM_ROUTES.newHireJourneys.index },
          {
            name: isFetched && newHireJourney?.newHire.fullName ? `${newHireJourney.newHire.fullName}` : "Onboarding",
            toUrl: newHireJourneyPathById(newHireJourney?.id),
          },
          { name: `Meeting '${truncateEnd(data?.name)}'` },
        ]}
      />
      {data && (
        <Card overflow="hidden">
          <ActionPageNewHireHeader newHireJourneyId={data?.newHireJourneyId} />
          <CardBody>
            {["skipped", "completed"].includes(data.status) ? (
              <PastMeeting meeting={data} />
            ) : (
              <MeetingForm
                data={data}
                includeHeader
                includeMenu
                includeStatus
                isPending={newHireJourney?.isPending ?? false}
              />
            )}
            <CollapsibleHistory entityId={data.id} entityType="Meeting" eventType="meeting" />
          </CardBody>
        </Card>
      )}
    </AppPage>
  );
}
